import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrganization } from '../../../context/OrgContext';
import { collection, getDocs, doc, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import InviteModal from '../../../components/modals/admin/InviteModal';
import NewInterfaceModal from './NewInterfaceModal';
import AdminLayout from '../../AdminLayout';
import { SectionHeader, InterfaceCard, InterfaceStatsBar } from '../../../components/dashboard';



const SectionDashboard = ({ isSubsection = false }) => {
  const params = useParams();
  const { institution } = useOrganization();
  const navigate = useNavigate();
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  
  // For nested routing, determine the actual section slug and parent path
  const actualSectionSlug = pathSegments[pathSegments.length - 1];
  const parentPath = pathSegments.slice(0, -1).join('/');

  // State management
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showNewInterfaceModal, setShowNewInterfaceModal] = useState(false);

  // Section and interfaces data
  const [section, setSection] = useState(null);
  const [interfaces, setInterfaces] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(true);
  const [interfacesLoading, setInterfacesLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch section data from institution document or parent subsection
  const fetchSection = async () => {
    if (!institution?.id || !actualSectionSlug) return;

    try {
      setSectionLoading(true);
      setError(null);

      if (isSubsection && pathSegments.length >= 3) {
        // For subsections, we need to get section info from parent interface's sections array
        // Navigate up the path to find the parent interface
        const parentSegments = pathSegments.slice(0, -1);
        const parentInterfaceSlug = parentSegments[parentSegments.length - 1];
        
        console.log('Fetching subsection data:', { pathSegments, actualSectionSlug, parentInterfaceSlug });

        // Build the collection path to the parent section
        let parentCollectionPath = `colleges/${institution.id}`;
        const grandparentSegments = parentSegments.slice(0, -1);
        
        for (let i = 0; i < grandparentSegments.length; i += 2) {
          // Add section collection
          parentCollectionPath += `/${grandparentSegments[i]}.section`;
          
          // If there's an interface after this section, add it as a document
          if (i + 1 < grandparentSegments.length) {
            parentCollectionPath += `/${grandparentSegments[i + 1]}`;
          }
        }
        
        console.log('Parent collection path:', parentCollectionPath);
        
        // Get all interfaces in the parent section to find the right one by slug
        const parentInterfacesRef = collection(db, parentCollectionPath);
        const parentInterfacesSnap = await getDocs(parentInterfacesRef);

        let parentInterface = null;
        parentInterfacesSnap.docs.forEach(doc => {
          const data = doc.data();
          if (data.type === 'interface') {
            const titleSlug = data.title ? data.title.toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim() : '';

            if (titleSlug === parentInterfaceSlug) {
              parentInterface = { id: doc.id, ...data };
            }
          }
        });

        if (!parentInterface) {
          console.error('Parent interface not found:', parentInterfaceSlug);
          setError('Parent interface not found');
          return;
        }

        console.log('Found parent interface:', parentInterface);
        console.log('Interface sections:', parentInterface.sections);

        const subsections = parentInterface.sections || [];
        const foundSubsection = subsections.find(s => s.slug === actualSectionSlug && s.type === 'subsection');

        console.log('Looking for subsection:', actualSectionSlug);
        console.log('Available subsections:', subsections.map(s => ({ name: s.name, slug: s.slug, type: s.type })));

        if (foundSubsection) {
          console.log('Found subsection:', foundSubsection);
          setSection(foundSubsection);
        } else {
          console.error('Subsection not found:', actualSectionSlug);
          setError('Subsection not found');
        }
      } else {
        // For top-level sections, get from institution document
        const institutionRef = doc(db, `colleges/${institution.id}`);
        const institutionDoc = await getDoc(institutionRef);
        const institutionData = institutionDoc.data() || {};
        const sections = institutionData.sections || [];

        const foundSection = sections.find(s => s.slug === actualSectionSlug);

        if (foundSection) {
          setSection(foundSection);
        } else {
          setError('Section not found');
        }
      }
    } catch (err) {
      console.error('Error fetching section:', err);
      setError('Failed to load section');
    } finally {
      setSectionLoading(false);
    }
  };

  // Fetch interfaces from section collection (works for both sections and subsections)
  const fetchInterfaces = async () => {
    if (!institution?.id || !actualSectionSlug) return;

    try {
      setInterfacesLoading(true);

      // Construct the collection path based on the path segments  
      let collectionPath = `colleges/${institution.id}`;
      
      // Build path step by step: section.collection -> interface -> subsection.collection -> interface...
      for (let i = 0; i < pathSegments.length; i += 2) {
        // Add section collection
        collectionPath += `/${pathSegments[i]}.section`;
        
        // If there's an interface after this section, add it as a document
        if (i + 1 < pathSegments.length) {
          collectionPath += `/${pathSegments[i + 1]}`;
        }
      }
      
      console.log('Interfaces collection path:', collectionPath);

      const interfacesRef = collection(db, collectionPath);
      const interfacesSnap = await getDocs(interfacesRef);

      const interfacesData = interfacesSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(item => item.type === 'interface'); // Filter out the _init document and other types

      // Sort by creation date (most recent first)
      interfacesData.sort((a, b) => {
        const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
        const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return bDate - aDate;
      });

      setInterfaces(interfacesData);
    } catch (error) {
      console.error('Error fetching interfaces:', error);
      setInterfaces([]);
    } finally {
      setInterfacesLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchSection();
    fetchInterfaces();
  }, [institution?.id, pathSegments.join('/'), isSubsection]);

  // Handle back navigation
  const handleBackClick = () => {
    if (parentPath) {
      // Navigate back to parent path
      navigate(`/${parentPath}`);
    } else {
      // Navigate to main dashboard
      navigate('/');
    }
  };

  // Handle settings navigation
  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Handle invite modal
  const handleInviteClick = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
  };

  // Handle new interface modal
  const handleNewInterfaceClick = () => {
    setShowNewInterfaceModal(true);
  };

  const handleCloseNewInterfaceModal = () => {
    setShowNewInterfaceModal(false);
  };

  // Handle successful interface addition
  const handleInterfaceAdded = () => {
    fetchInterfaces();
    setShowNewInterfaceModal(false);
  };

  // Handle interface click
  const handleInterfaceClick = (interfaceItem) => {
    // Create slug from title
    const newInterfaceSlug = interfaceItem.title.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    // Navigate to interface by appending to current path
    const currentPath = pathSegments.join('/');
    navigate(`/${currentPath}/${newInterfaceSlug}`);
  };


  // Loading state
  if (sectionLoading) {
    return (
      <AdminLayout>
        <div className="h-full w-full text-white px-8 pt-8 flex items-center justify-center">
          <div className="text-center">
            <div className="text-xl">Loading section...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error || !section) {
    return (
      <AdminLayout>
        <div className="h-full w-full text-white px-8 pt-8 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Section Not Found</h1>
            <p className="text-xl text-gray-400 mb-8">{error || 'The requested section could not be found.'}</p>
            <button
              onClick={handleBackClick}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-full w-full text-white px-8 pt-8 flex flex-col overflow-x-clip overflow-y-auto">
        {/* Header */}
        <SectionHeader
          name={section.name}
          onBackClick={handleBackClick}
          onSettingsClick={handleSettingsClick}
          onInviteClick={handleInviteClick}
          onNewClick={handleNewInterfaceClick}
          newButtonText="New Interface"
        />

        {/* Interfaces Count */}
        <InterfaceStatsBar
          itemCount={interfaces.length}
          itemType="Interfaces"
          createdAt={section.createdAt}
          isLoading={interfacesLoading}
        />

        {/* Interfaces Grid */}
        <div className={`${interfaces.length === 0 ? "flex-1" : "h-fit"} w-full flex`}>
          {interfacesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
              {[1, 2, 3].map(placeholder => (
                <div className="w-full h-64 relative" key={placeholder}>
                  <div className="p-4 w-full h-48 bg-[#1e1e1e] border border-[#ffffff10] rounded-xl flex flex-col justify-between animate-pulse">
                    <div className="flex space-x-2 items-center h-fit">
                      <div className="p-3 h-fit w-fit bg-[#ffffff20] rounded-lg">
                        <div className="w-6 h-6 bg-[#ffffff30] rounded"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-5 bg-[#ffffff20] rounded mb-2"></div>
                        <div className="h-4 bg-[#ffffff10] rounded w-3/4"></div>
                      </div>
                    </div>
                    <div className="flex space-x-2 items-center h-fit">
                      <div className="p-3 h-fit w-fit">
                        <div className="w-5 h-5 bg-[#ffffff20] rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <div className="h-4 bg-[#ffffff10] rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                  <div className="w-full h-16 bg-[#1a1a1a] border-x border-b border-[#ffffff10] rounded-b-xl animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
              <InterfaceCard
                title="New Interface"
                isNew={true}
                onClick={handleNewInterfaceClick}
              />
              {interfaces.map(interfaceItem => (
                <InterfaceCard
                  key={interfaceItem.id}
                  title={interfaceItem.title}
                  createdAt={interfaceItem.createdAt}
                  onClick={() => handleInterfaceClick(interfaceItem)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modals */}
        {showInviteModal && (
            <InviteModal
                isOpen={showInviteModal}
                onClose={handleCloseInviteModal}
            />
        )}

        {showNewInterfaceModal && (
            <NewInterfaceModal
                isOpen={showNewInterfaceModal}
                onClose={handleCloseNewInterfaceModal}
                onInterfaceAdded={handleInterfaceAdded}
                pathSegments={pathSegments}
            />
        )}
      </div>
    </AdminLayout>
  );
};

export default SectionDashboard;